import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Divider, Header, Tab } from 'semantic-ui-react';
import { RiChatSmileAiLine } from 'react-icons/ri';
import { usePopup } from '../../../lib/popup';

import selectors from '../../../selectors';
import Config from '../../../constants/Config';
import InformationEdit from './InformationEdit';
import DeleteStep from '../../DeleteStep';
import AgentPromptModal from '../../AgentPromptModal';

import styles from './GeneralPane.module.scss';

const GeneralPane = React.memo(({ projectId, name, onUpdate, onDelete }) => {
  const [t] = useTranslation();
  const accessToken = useSelector(selectors.selectAccessToken);
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [agentConfig, setAgentConfig] = useState({
    systemPrompt: '',
    agentEnabled: false,
    canon: null,
  });
  const [isLoadingAgent, setIsLoadingAgent] = useState(false);

  const DeletePopup = usePopup(DeleteStep);

  const handleOpenAgentModal = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    setIsLoadingAgent(true);
    try {
      // Use Config.SERVER_BASE_URL to ensure requests hit the backend domain, not the frontend
      const response = await fetch(
        `${Config.SERVER_BASE_URL}/api/projects/${projectId}/agent-config`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAgentConfig(data);
      } else {
        console.error('Error loading agent config:', response.status);
      }
    } catch (error) {
      console.error('Error loading agent config:', error);
    } finally {
      setIsLoadingAgent(false);
    }
    setIsAgentModalOpen(true);
  }, [projectId, accessToken]);

  const handleCloseAgentModal = useCallback(() => {
    setIsAgentModalOpen(false);
  }, []);

  const handleSaveAgentConfig = useCallback(
    async config => {
      if (!accessToken) {
        return false;
      }
      try {
        const response = await fetch(
          `${Config.SERVER_BASE_URL}/api/projects/${projectId}/agent-config`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            credentials: 'include',
            body: JSON.stringify(config),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP error ${response.status}`);
        }

        console.log('✅ Agent config saved');
        return true;
      } catch (error) {
        console.error('Error saving agent config:', error);
        throw error;
      }
    },
    [projectId, accessToken]
  );

  return (
    <Tab.Pane attached={false} className={styles.wrapper}>
      <InformationEdit
        defaultData={{
          name,
        }}
        onUpdate={onUpdate}
      />

      <Divider horizontal section>
        <Header as="h4">
          {t('common.dangerZone', {
            context: 'title',
          })}
        </Header>
      </Divider>
      <div className={styles.action}>
        <DeletePopup
          title="common.deleteProject"
          content="common.areYouSureYouWantToDeleteThisProject"
          buttonContent="action.deleteProject"
          onConfirm={onDelete}
        >
          <Button className={styles.actionButton}>
            {t('action.deleteProject', {
              context: 'title',
            })}
          </Button>
        </DeletePopup>
      </div>

      {/* Botón AI de configuración */}
      <Button
        size="large"
        loading={isLoadingAgent}
        onClick={handleOpenAgentModal}
        className={styles.agentButton}
      >
        <RiChatSmileAiLine className={styles.agentIcon} />
        AI Agent Configuration
      </Button>

      <AgentPromptModal
        isOpen={isAgentModalOpen}
        entityType="project"
        entityName={name}
        systemPrompt={agentConfig.systemPrompt}
        agentEnabled={agentConfig.agentEnabled}
        canonDocument={agentConfig.canon}
        projectId={projectId}
        onSave={handleSaveAgentConfig}
        onClose={handleCloseAgentModal}
      />
    </Tab.Pane>
  );
});

GeneralPane.propTypes = {
  projectId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default GeneralPane;
